import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CreditCardOutlined, 
  DashboardOutlined, 
  SafetyOutlined, 
  ThunderboltOutlined,
  GlobalOutlined,
  RocketOutlined,
  BulbOutlined
} from '@ant-design/icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

export const FeaturesSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();

  const features: Feature[] = [
    {
      icon: <CreditCardOutlined className="text-5xl" />,
      title: t('features.securePayment.title'),
      description: t('features.securePayment.description'),
      color: "from-slate-600 to-slate-700"
    },
    {
      icon: <DashboardOutlined className="text-5xl" />,
      title: t('features.realtimeDashboard.title'),
      description: t('features.realtimeDashboard.description'),
      color: "from-gray-600 to-gray-700"
    },
    {
      icon: <SafetyOutlined className="text-5xl" />,
      title: t('features.advancedSecurity.title'),
      description: t('features.advancedSecurity.description'),
      color: "from-zinc-600 to-zinc-700"
    },
    {
      icon: <ThunderboltOutlined className="text-5xl" />,
      title: t('features.lightningFast.title'),
      description: t('features.lightningFast.description'),
      color: "from-neutral-600 to-neutral-700"
    },
    {
      icon: <GlobalOutlined className="text-5xl" />,
      title: t('features.multiLanguage.title'),
      description: t('features.multiLanguage.description'),
      color: "from-stone-600 to-stone-700"
    },
    {
      icon: <RocketOutlined className="text-5xl" />,
      title: t('features.easyIntegration.title'),
      description: t('features.easyIntegration.description'),
      color: "from-slate-700 to-slate-800"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <div className="relative h-full flex flex-col justify-center">
      {/* Feature Cards */}
      <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden">
        {features.map((feature, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              index === currentIndex
                ? 'opacity-100 translate-y-0'
                : index < currentIndex
                ? 'opacity-0 -translate-y-full'
                : 'opacity-0 translate-y-full'
            }`}
          >
            <div className={`bg-gradient-to-br ${feature.color} p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl h-full flex flex-col justify-center items-center text-center text-white backdrop-blur-sm bg-opacity-80`}>
              <div className="mb-4 sm:mb-6">
                <div className="text-4xl sm:text-5xl">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-poppins-700 mb-3 sm:mb-4">
                {feature.title}
              </h3>
              <p className="text-sm sm:text-base font-poppins-400 opacity-80 px-2">
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-4 sm:mt-6">
        {features.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`transition-all duration-300 rounded-full min-h-[4px] min-w-[4px] flex items-center justify-center ${
              index === currentIndex
                ? 'w-8 h-3 bg-white'
                : 'w-3 h-3 bg-white/40'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Tips Section */}
      <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white/5 backdrop-blur-md rounded-xl sm:rounded-2xl border border-white/10">
        <h4 className="text-white font-poppins-600 text-base sm:text-lg mb-2 sm:mb-3 flex items-center gap-2">
          <BulbOutlined className="text-xl sm:text-2xl text-gray-300" /> {t('quickTips')}
        </h4>
        <ul className="space-y-2 text-white/70 font-poppins-400 text-xs sm:text-sm">
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-0.5 sm:mt-1">✓</span>
            <span>{t('tips.useRegisteredEmail')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-0.5 sm:mt-1">✓</span>
            <span>{t('tips.enable2FA')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-0.5 sm:mt-1">✓</span>
            <span>{t('tips.contactSupport')}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
